import {
  AbstractWalletPlugin,
  LoginContext,
  ResolvedSigningRequest,
  Session,
  SessionKit,
  TransactContext,
  WalletPluginConfig,
  WalletPluginLoginResponse,
  WalletPluginMetadata,
  WalletPluginSignResponse,
} from '@wharfkit/session'
import { PermissionLevel, PrivateKey } from '@wharfkit/antelope'
import { WalletPluginPrivateKey } from '@wharfkit/wallet-plugin-privatekey'
import { accountManager, migrateLegacyEmbeddedWallet } from './account-manager.js'
import {
  EMBEDDED_WALLET_PASSWORD_SESSION_KEY,
  EMBEDDED_WALLET_STORAGE_KEY,
} from './embedded-constants.js'
import { fetchAccountNameByPublicKey } from './resolve-eos-account.js'

/** Отклонение unlock-модалки: сменить аккаунт → показать создание кошелька */
export const EMBEDDED_SWITCH_ACCOUNT = 'SWITCH_ACCOUNT'

function removeLegacyEmbeddedKeys(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(EMBEDDED_WALLET_STORAGE_KEY)
    localStorage.removeItem('wharfkit.embedded.encryptedKey')
  } catch {
    // ignore
  }
}

const translations = {
  en: {
    title: 'Embedded Wallet',
    pluginDescription: 'Private key stored encrypted in localStorage (AES-GCM + PBKDF2)',
    enterPassword: 'Enter password',
    enterPrivateKey: 'Private Key',
    save: 'Save',
    login: 'Login',
    wrongPassword: 'Wrong password',
    keyNotFound: 'Account not found for this key',
    unlockBody: 'Unlock your saved wallet on this device.',
    createBody: 'Save an EOS private key encrypted on this device.',
    rpcError: 'Could not resolve account for this key',
    passwordRequired: 'Password is required',
    privateKeyRequired: 'Private key is required',
    cancel: 'Cancel',
    emptyWallet: 'Embedded wallet is empty',
    invalidStorage: 'Invalid embedded wallet storage format',
    unsupportedFormat: 'Unsupported embedded wallet format',
    invalidPrivateKey: 'Invalid private key (use EOS base58 key, e.g. starting with 5)',
    switchAccount: 'Switch account',
    switchAccountHint: 'This will remove saved key',
    forgetConfirm: 'Are you sure? Your encrypted key will be deleted.',
  },
  ru: {
    title: 'Встроенный кошелёк',
    pluginDescription:
      'Приватный ключ хранится в зашифрованном виде в браузере (AES-GCM + PBKDF2)',
    enterPassword: 'Введите пароль',
    enterPrivateKey: 'Приватный ключ',
    save: 'Сохранить',
    login: 'Войти',
    wrongPassword: 'Неверный пароль',
    keyNotFound: 'Аккаунт не найден для этого ключа',
    unlockBody: 'Разблокируйте сохранённый кошелёк на этом устройстве.',
    createBody: 'Сохраните приватный ключ EOS в зашифрованном виде на этом устройстве.',
    rpcError: 'Не удалось получить аккаунт по ключу',
    passwordRequired: 'Нужен пароль',
    privateKeyRequired: 'Нужен приватный ключ',
    cancel: 'Отмена',
    emptyWallet: 'Встроенный кошелёк пуст',
    invalidStorage: 'Неверный формат хранилища кошелька',
    unsupportedFormat: 'Неподдерживаемый формат кошелька',
    invalidPrivateKey: 'Неверный приватный ключ (формат EOS base58, обычно с 5…)',
    switchAccount: 'Сменить аккаунт',
    switchAccountHint: 'Сохранённый ключ будет удалён',
    forgetConfirm: 'Вы уверены? Зашифрованный ключ будет удалён.',
  },
  zh: {
    title: '内置钱包',
    pluginDescription: '私钥以加密形式保存在浏览器中（AES-GCM + PBKDF2）',
    enterPassword: '输入密码',
    enterPrivateKey: '私钥',
    save: '保存',
    login: '登录',
    wrongPassword: '密码错误',
    keyNotFound: '未找到此密钥的账户',
    unlockBody: '在此设备上解锁已保存的钱包。',
    createBody: '在此设备上加密保存 EOS 私钥。',
    rpcError: '无法根据密钥解析账户',
    passwordRequired: '需要密码',
    privateKeyRequired: '需要私钥',
    cancel: '取消',
    emptyWallet: '内置钱包为空',
    invalidStorage: '钱包存储格式无效',
    unsupportedFormat: '不支持的钱包格式',
    invalidPrivateKey: '私钥无效（请使用 EOS base58 格式，通常以 5 开头）',
    switchAccount: '切换账户',
    switchAccountHint: '这将删除保存的密钥',
    forgetConfirm: '确定吗？加密密钥将被删除。',
  },
} as const

type LangKey = keyof typeof translations
type EmbeddedStrings = (typeof translations)[LangKey]

function getLang(): LangKey {
  if (typeof window === 'undefined') return 'ru'
  const raw = (
    localStorage.getItem('selectedLanguage') ||
    localStorage.getItem('language') ||
    'ru'
  ).toLowerCase()
  if (raw.startsWith('ru')) return 'ru'
  if (raw.startsWith('zh')) return 'zh'
  return 'en'
}

function getTranslations(): EmbeddedStrings {
  const lang = getLang()
  return translations[lang] ?? translations.en
}

async function getStoredPrivateKeyWif(password: string): Promise<string> {
  const t = getTranslations()
  await accountManager.init()
  await migrateLegacyEmbeddedWallet(password)
  const acc = accountManager.getActiveAccount()
  if (!acc) throw new Error(t.emptyWallet)
  const unlocked = await accountManager.unlock(acc.name, password)
  const wif = unlocked.privateKey
  unlocked.privateKey = ''
  return wif
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** In-memory unlocked WIF — never persist raw password in sessionStorage. */
const WIF_CACHE_TTL_MS = 15 * 60 * 1000
let memoryUnlockedWif: { wif: string; expiresAt: number } | null = null
let visibilityListenerBound = false

function clearMemoryUnlockedWif(): void {
  memoryUnlockedWif = null
}

function cacheUnlockedWif(wif: string): void {
  memoryUnlockedWif = { wif, expiresAt: Date.now() + WIF_CACHE_TTL_MS }
  try {
    sessionStorage.removeItem(EMBEDDED_WALLET_PASSWORD_SESSION_KEY)
  } catch {
    /* ignore */
  }
  bindVisibilityClearOnce()
}

function getCachedUnlockedWif(): string | null {
  if (!memoryUnlockedWif) return null
  if (Date.now() > memoryUnlockedWif.expiresAt) {
    clearMemoryUnlockedWif()
    return null
  }
  return memoryUnlockedWif.wif
}

function bindVisibilityClearOnce(): void {
  if (typeof window === 'undefined' || visibilityListenerBound) return
  visibilityListenerBound = true
  const clear = () => {
    if (document.visibilityState === 'hidden') clearMemoryUnlockedWif()
  }
  document.addEventListener('visibilitychange', clear)
  window.addEventListener('pagehide', () => clearMemoryUnlockedWif())
  window.addEventListener('blur', () => {
    // Soft clear on blur — reduces XSS window if tab is backgrounded
    clearMemoryUnlockedWif()
  })
}

const SVG_EYE = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>`

const SVG_EYE_OFF = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>`

/** DOM-only modal (no WharfKit `prompt`): unlock wallet with password + eye toggle. */
function showUnlockPasswordModal(t: EmbeddedStrings): Promise<string> {
  return new Promise((resolve, reject) => {
    const lang = getLang()
    const overlay = document.createElement('div')
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;font-family:system-ui,-apple-system,sans-serif;'
    overlay.innerHTML = `
      <div style="position:relative;z-index:100000;background:#1f2937;color:#fff;border-radius:12px;max-width:420px;width:100%;padding:20px;box-sizing:border-box;border:1px solid #374151;">
        <h2 style="margin:0 0 8px;font-size:18px;font-weight:600;color:#fff;">${escapeHtml(t.title)}</h2>
        <p style="margin:0 0 16px;font-size:14px;color:#e5e7eb;line-height:1.4;">${escapeHtml(t.unlockBody)}</p>
        <div style="display:flex;gap:8px;align-items:stretch;margin-bottom:8px;">
          <input id="password-input" type="password" autocomplete="off" placeholder="${escapeHtml(t.enterPassword)}" style="flex:1;min-width:0;height:40px;border-radius:8px;border:1px solid #4b5563;padding:0 12px;font-size:14px;background:#111827;color:#fff;box-sizing:border-box;" />
          <button type="button" id="password-toggle" aria-label="toggle password visibility" style="flex-shrink:0;width:44px;display:flex;align-items:center;justify-content:center;border-radius:8px;border:1px solid #4b5563;background:#374151;color:#e5e7eb;cursor:pointer;padding:0;">${SVG_EYE}</button>
        </div>
        <div id="embedded-error" style="
          display: none;
          color: #f87171;
          font-size: 13px;
          margin-top: 8px;
          padding: 8px 12px;
          background: rgba(239,68,68,0.1);
          border-radius: 6px;
          border: 1px solid rgba(239,68,68,0.3);
        "></div>
        <div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;">
          <button type="button" id="cancel-btn" style="padding:8px 16px;border-radius:8px;border:1px solid #4b5563;background:transparent;color:#e5e7eb;cursor:pointer;font-size:14px;">${escapeHtml(t.cancel)}</button>
          <button type="button" id="login-btn" style="padding:8px 16px;border-radius:8px;border:none;background:#7c3aed;color:#fff;cursor:pointer;font-size:14px;font-weight:600;">${escapeHtml(t.login)}</button>
        </div>
        <button id="btn-switch" style="
          background: none;
          border: none;
          color: #6b7280;
          font-size: 12px;
          cursor: pointer;
          margin-top: 12px;
          text-decoration: underline;
          display: block;
          width: 100%;
          text-align: center;
        ">${lang === 'ru' ? 'Сменить аккаунт' : lang === 'zh' ? '切换账户' : 'Switch account'}</button>
      </div>
    `
    document.body.appendChild(overlay)

    const passwordInput = overlay.querySelector('#password-input') as HTMLInputElement
    const toggleBtn = overlay.querySelector('#password-toggle') as HTMLButtonElement
    const errorMsg = overlay.querySelector('#embedded-error') as HTMLElement
    const btnSave = overlay.querySelector('#login-btn') as HTMLButtonElement
    const btnSwitch = overlay.querySelector('#btn-switch') as HTMLElement

    let visible = false
    toggleBtn.addEventListener('click', () => {
      visible = !visible
      passwordInput.type = visible ? 'text' : 'password'
      toggleBtn.innerHTML = visible ? SVG_EYE_OFF : SVG_EYE
    })

    const cleanup = () => {
      if (overlay.parentNode) document.body.removeChild(overlay)
    }

    btnSave.addEventListener('click', async () => {
      const password = passwordInput.value.trim()
      if (!password) return

      btnSave.disabled = true
      btnSave.textContent = '...'

      errorMsg.style.display = 'none'

      try {
        const wif = await getStoredPrivateKeyWif(password)
        cacheUnlockedWif(wif)
        cleanup()
        resolve(wif)
      } catch {
        errorMsg.textContent =
          lang === 'ru'
            ? '❌ Неверный пароль. Попробуйте ещё раз.'
            : lang === 'zh'
              ? '❌ 密码错误，请重试。'
              : '❌ Wrong password. Please try again.'
        errorMsg.style.display = 'block'

        btnSave.disabled = false
        btnSave.textContent = lang === 'ru' ? 'Войти' : 'Unlock'

        passwordInput.value = ''
        passwordInput.focus()
      }
    })

    overlay.querySelector('#cancel-btn')!.addEventListener('click', () => {
      cleanup()
      reject(new Error('Canceled'))
    })

    btnSwitch.addEventListener('click', async () => {
      const ok =
        lang === 'ru'
          ? confirm('Удалить сохранённый кошелёк?')
          : confirm('Remove saved wallet?')
      if (!ok) return
      try {
        sessionStorage.removeItem(EMBEDDED_WALLET_PASSWORD_SESSION_KEY)
      } catch {
        // ignore
      }
      clearMemoryUnlockedWif()
      try {
        await accountManager.init()
        await accountManager.resetWallet()
      } catch {
        // ignore
      }
      removeLegacyEmbeddedKeys()
      cleanup()
      reject(new Error(EMBEDDED_SWITCH_ACCOUNT))
    })

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        cleanup()
        reject(new Error('Canceled'))
      }
    })

    passwordInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') btnSave.click()
    })

    passwordInput.focus()
  })
}

/** DOM-only modal: new wallet — private key textarea + password with eye + Save. */
function showCreateWalletModal(t: EmbeddedStrings): Promise<{ privateKey: string; password: string }> {
  return new Promise((resolve, reject) => {
    const overlay = document.createElement('div')
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;font-family:system-ui,-apple-system,sans-serif;'
    overlay.innerHTML = `
      <div style="position:relative;z-index:100000;background:#1f2937;color:#fff;border-radius:12px;max-width:420px;width:100%;padding:20px;box-sizing:border-box;border:1px solid #374151;">
        <h2 style="margin:0 0 8px;font-size:18px;font-weight:600;color:#fff;">${escapeHtml(t.title)}</h2>
        <p style="margin:0 0 16px;font-size:14px;color:#e5e7eb;line-height:1.4;">${escapeHtml(t.createBody)}</p>
        <div id="pc-embedded-err" style="min-height:20px;margin-bottom:8px;font-size:13px;color:#f87171;"></div>
        <label style="display:block;font-size:13px;color:#9ca3af;margin-bottom:6px;">${escapeHtml(t.enterPrivateKey)}</label>
        <textarea id="private-key-input" rows="4" placeholder="5..." style="width:100%;box-sizing:border-box;border-radius:8px;border:1px solid #4b5563;padding:10px 12px;font-size:13px;background:#111827;color:#fff;margin-bottom:14px;resize:vertical;min-height:88px;font-family:ui-monospace,monospace;"></textarea>
        <label style="display:block;font-size:13px;color:#9ca3af;margin-bottom:6px;">${escapeHtml(t.enterPassword)}</label>
        <div style="display:flex;gap:8px;align-items:stretch;margin-bottom:16px;">
          <input id="password-input" type="password" autocomplete="off" placeholder="${escapeHtml(t.enterPassword)}" style="flex:1;min-width:0;height:40px;border-radius:8px;border:1px solid #4b5563;padding:0 12px;font-size:14px;background:#111827;color:#fff;box-sizing:border-box;" />
          <button type="button" id="password-toggle" aria-label="toggle password visibility" style="flex-shrink:0;width:44px;display:flex;align-items:center;justify-content:center;border-radius:8px;border:1px solid #4b5563;background:#374151;color:#e5e7eb;cursor:pointer;padding:0;">${SVG_EYE}</button>
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;">
          <button type="button" id="cancel-btn" style="padding:8px 16px;border-radius:8px;border:1px solid #4b5563;background:transparent;color:#e5e7eb;cursor:pointer;font-size:14px;">${escapeHtml(t.cancel)}</button>
          <button type="button" id="save-btn" style="padding:8px 16px;border-radius:8px;border:none;background:#7c3aed;color:#fff;cursor:pointer;font-size:14px;font-weight:600;">${escapeHtml(t.save)}</button>
        </div>
      </div>
    `
    document.body.appendChild(overlay)

    const ta = overlay.querySelector('#private-key-input') as HTMLTextAreaElement
    const passwordInput = overlay.querySelector('#password-input') as HTMLInputElement
    const toggleBtn = overlay.querySelector('#password-toggle') as HTMLButtonElement
    const errEl = overlay.querySelector('#pc-embedded-err') as HTMLDivElement

    let visible = false
    toggleBtn.addEventListener('click', () => {
      visible = !visible
      passwordInput.type = visible ? 'text' : 'password'
      toggleBtn.innerHTML = visible ? SVG_EYE_OFF : SVG_EYE
    })

    const cleanup = () => {
      if (overlay.parentNode) document.body.removeChild(overlay)
    }

    const submit = () => {
      errEl.textContent = ''
      const pk = ta.value.trim()
      const pwd = passwordInput.value.trim()
      if (!pk) {
        errEl.textContent = t.privateKeyRequired
        return
      }
      if (!pwd) {
        errEl.textContent = t.passwordRequired
        return
      }
      try {
        PrivateKey.from(pk)
      } catch {
        errEl.textContent = t.invalidPrivateKey
        return
      }
      cleanup()
      resolve({ privateKey: pk, password: pwd })
    }

    overlay.querySelector('#save-btn')!.addEventListener('click', submit)
    overlay.querySelector('#cancel-btn')!.addEventListener('click', () => {
      cleanup()
      reject(new Error('Canceled'))
    })

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        cleanup()
        reject(new Error('Canceled'))
      }
    })

    passwordInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit()
    })

    ta.focus()
  })
}

/**
 * Telegram WebApp (initData): только embedded, без SessionKit.login и без списка кошельков.
 * Модалки unlock/create → Session + persistSession в SessionKit storage.
 */
export async function telegramEmbeddedDirectLogin(sessionKit: SessionKit): Promise<Session> {
  console.log('[TG Login] Starting...')
  try {
    if (typeof window === 'undefined') {
      throw new Error('WalletPluginEmbedded can be used only in the browser')
    }
    const chain = sessionKit.chains[0]
    if (!chain) throw new Error('No chain available for login')

    await accountManager.init()

    const t = getTranslations()
    const storedWalletExists = hasEmbeddedWallet()
    let privateKey: string
    let passwordForSession: string | null = null

    if (storedWalletExists) {
      try {
        privateKey = await showUnlockPasswordModal(t)
      } catch (e) {
        if (e instanceof Error && e.message === EMBEDDED_SWITCH_ACCOUNT) {
          const created = await showCreateWalletModal(t)
          passwordForSession = created.password
          await accountManager.init()
          const pk = PrivateKey.from(created.privateKey)
          const actor = await fetchAccountNameByPublicKey(pk.toPublic().toLegacyString())
          await accountManager.addAccount(created.privateKey, {
            encryptWithPassword: created.password,
            accountName: actor,
          })
          privateKey = created.privateKey
        } else {
          throw e
        }
      }
    } else {
      const created = await showCreateWalletModal(t)
      passwordForSession = created.password
      await accountManager.init()
      const pk = PrivateKey.from(created.privateKey)
      const actor = await fetchAccountNameByPublicKey(pk.toPublic().toLegacyString())
      await accountManager.addAccount(created.privateKey, {
        encryptWithPassword: created.password,
        accountName: actor,
      })
      privateKey = created.privateKey
    }

    // Cache WIF in memory only — never persist password in sessionStorage
    cacheUnlockedWif(privateKey)

    console.log('[TG Login] Got privateKey, getting account...')
    const pk = PrivateKey.from(privateKey)
    const publicKeyStr = pk.toPublic().toLegacyString()
    const actor = await fetchAccountNameByPublicKey(publicKeyStr)
    console.log('[TG Login] Actor:', actor)

    const permissionLevel = PermissionLevel.from({ actor, permission: 'active' })
    const embeddedPlugin = new WalletPluginEmbedded()
    embeddedPlugin.setPrivateKey(pk)

    const session = new Session(
      {
        chain,
        permissionLevel,
        walletPlugin: embeddedPlugin,
      },
      {
        appName: sessionKit.appName,
        fetch: sessionKit.fetch,
        transactPlugins: sessionKit.transactPlugins,
        transactPluginsOptions: sessionKit.transactPluginsOptions,
        ui: sessionKit.ui,
      }
    )

    console.log('[TG Login] Session created:', session.actor.toString())
    console.log('[TG Login] Persisting session...')
    await sessionKit.persistSession(session, true)

    const sessionsAfter = await sessionKit.getSessions()
    console.log('[TG Login] Sessions after persist:', sessionsAfter)

    console.log('[TG Login] Done! Returning session.')
    return session
  } catch (e) {
    console.error('[TG Login] ERROR:', e)
    throw e
  }
}

function getArbitraryCredentials(context: LoginContext): {
  password: string
  privateKey: string
} {
  const arbitrary = (context.arbitrary || {}) as Record<string, unknown>
  const password =
    (arbitrary.embeddedPassword ||
      arbitrary.walletPassword ||
      arbitrary.password ||
      arbitrary.embedded_wallet_password ||
      '') as string
  const privateKey =
    (arbitrary.embeddedPrivateKey ||
      arbitrary.privateKey ||
      arbitrary.eosPrivateKey ||
      arbitrary.walletPrivateKey ||
      '') as string
  return { password: String(password), privateKey: String(privateKey) }
}

export class WalletPluginEmbedded extends AbstractWalletPlugin {
  id = 'embedded'

  privateKeyPlugin: WalletPluginPrivateKey | null = null

  /** Sets decrypted key for in-memory signing (not persisted into session storage). */
  public setPrivateKey(pk: PrivateKey): void {
    this.privateKeyPlugin = new WalletPluginPrivateKey(pk)
  }

  config: WalletPluginConfig = {
    requiresChainSelect: false,
    requiresPermissionSelect: false,
  }

  // AbstractWalletPlugin's constructor does `this.metadata = new WalletPluginMetadata({})`.
  // A no-op setter accepts that write; the getter always returns our computed metadata.
  // @ts-expect-error TS disallows accessor pair overriding an inherited property; runtime is correct.
  set metadata(_value: WalletPluginMetadata) {
    // intentionally empty
  }

  get metadata(): WalletPluginMetadata {
    return WalletPluginMetadata.from({
      name: 'Встроенный кошелёк',
      description: 'Приватный ключ зашифрован на этом устройстве',
      logo:
        typeof window !== 'undefined'
          ? `${window.location.origin}/icons/webdexlogo.png`
          : 'https://webdex.space/icons/webdexlogo.png',
    })
  }

  async login(context: LoginContext): Promise<WalletPluginLoginResponse> {
    try {
      if (typeof window === 'undefined') {
        throw new Error('WalletPluginEmbedded can be used only in the browser')
      }

      console.log('[embedded] login start')

      await accountManager.init()

      const chain = context.chain ?? context.chains[0]
      if (!chain) throw new Error('No chain available for login')

      let useStoredWallet = hasEmbeddedWallet()
      const { password: arbPass, privateKey: arbKey } = getArbitraryCredentials(context)
      const arbPassword = String(arbPass).trim()
      const arbPrivateKey = String(arbKey).trim()
      const t = getTranslations()

      let password: string
      let newWalletPrivateKey: string | undefined

      if (useStoredWallet) {
        if (arbPassword) {
          password = arbPassword
          newWalletPrivateKey = undefined
        } else {
          try {
            const wif = await showUnlockPasswordModal(t)
            password = '__via_modal__'
            newWalletPrivateKey = wif
            useStoredWallet = false
          } catch (e) {
            if (e instanceof Error && e.message === EMBEDDED_SWITCH_ACCOUNT) {
              const created = await showCreateWalletModal(t)
              password = created.password
              newWalletPrivateKey = created.privateKey
              useStoredWallet = false
            } else {
              throw e
            }
          }
        }
      } else if (arbPassword && arbPrivateKey) {
        password = arbPassword
        newWalletPrivateKey = arbPrivateKey
      } else {
        const created = await showCreateWalletModal(t)
        password = created.password
        newWalletPrivateKey = created.privateKey
      }

      console.log('[embedded] password received')

      let privateKey: string
      if (useStoredWallet) {
        privateKey = await getStoredPrivateKeyWif(arbPassword)
      } else {
        if (!newWalletPrivateKey) {
          throw new Error(t.privateKeyRequired)
        }
        if (password !== '__via_modal__') {
          const pk = PrivateKey.from(newWalletPrivateKey)
          const publicKeyStr = pk.toPublic().toLegacyString()
          const actor = await fetchAccountNameByPublicKey(publicKeyStr)
          await accountManager.addAccount(newWalletPrivateKey, {
            encryptWithPassword: password,
            accountName: actor,
          })
        }
        privateKey = newWalletPrivateKey
      }

      console.log('[embedded] privateKey ready')

      cacheUnlockedWif(privateKey)

      this.privateKeyPlugin = new WalletPluginPrivateKey(privateKey)

      const pk = PrivateKey.from(privateKey)
      const publicKey = pk.toPublic()
      const publicKeyStr = publicKey.toLegacyString()

      const actor = await fetchAccountNameByPublicKey(publicKeyStr)

      const permissionLevel = PermissionLevel.from({ actor, permission: 'active' })
      const nextContext = {
        ...context,
        chain,
        permissionLevel,
      } as LoginContext
      Object.setPrototypeOf(nextContext, Object.getPrototypeOf(context))

      console.log('[embedded] calling privateKeyPlugin.login')
      const loginResult = await this.privateKeyPlugin!.login(nextContext)
      try {
        const ui = context.ui as unknown as { close?: () => void }
        if (context.ui && typeof ui.close === 'function') {
          ui.close()
        }
      } catch (e) {
        console.warn('[embedded] ui.close failed', e)
      }
      console.log('[embedded] loginResult received', loginResult)

      const ctxAny = nextContext as LoginContext & { transactPlugins?: unknown }
      const lrAny = loginResult as WalletPluginLoginResponse & {
        session?: { transactPlugins?: unknown }
      }
      if (ctxAny.transactPlugins && lrAny.session) {
        ;(lrAny.session as { transactPlugins: unknown }).transactPlugins = ctxAny.transactPlugins
      }

      console.log('[embedded] returning loginResult')
      return loginResult
    } catch (e) {
      console.error('[embedded] login error', e)
      throw e
    }
  }

  async sign(resolved: ResolvedSigningRequest, context: TransactContext): Promise<WalletPluginSignResponse> {
    if (!this.privateKeyPlugin) {
      const t = getTranslations()
      const cachedWif = getCachedUnlockedWif()
      if (cachedWif) {
        try {
          this.setPrivateKey(PrivateKey.from(cachedWif))
        } catch {
          clearMemoryUnlockedWif()
        }
      }

      // One-time migration: clear any legacy plaintext password left in sessionStorage
      try {
        sessionStorage.removeItem(EMBEDDED_WALLET_PASSWORD_SESSION_KEY)
      } catch {
        /* ignore */
      }

      if (!this.privateKeyPlugin) {
        const wif = await showUnlockPasswordModal(t)
        this.setPrivateKey(PrivateKey.from(wif))
      }
    }
    if (!this.privateKeyPlugin) {
      throw new Error('WalletPluginEmbedded failed to initialize private key')
    }
    return this.privateKeyPlugin.sign(resolved, context)
  }
}

export function hasEmbeddedWallet(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (localStorage.getItem(EMBEDDED_WALLET_STORAGE_KEY)) return true
  } catch {
    // ignore
  }
  try {
    return accountManager.isLoaded() && accountManager.hasAccounts()
  } catch {
    return false
  }
}

export function clearEmbeddedWallet(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(EMBEDDED_WALLET_STORAGE_KEY)
    localStorage.removeItem('wharfkit.embedded.encryptedKey')
  } catch {
    // ignore
  }
}
